import { useIntl } from 'react-intl'

import { ButtonGroup, FormattedMessage } from '@subaio/components'

import type { OnLogEvent } from 'lib/container'

import type {
	Handlers,
	OpportunityCarInsuranceStep,
	OpportunityCarInsuranceStepType,
} from 'containers/OpportunityCarInsurance/handlers'

import { ButtonLink, ButtonSolid, ButtonText } from 'components/Buttons'
import { Card } from 'components/Card'
import { IconInfo, IconLink } from 'components/Icons'
import { ButtonLabel, Header } from 'components/Layout'
import { useModal } from 'components/Modal'

import { Title } from '../Title'
import { Content } from './Content'
import { Modal } from './Modal'
import { policies } from './policies'

type Props = TaggedType<OpportunityCarInsuranceStep, OpportunityCarInsuranceStepType.TermsAndConditions> & {
	onReturnToComparison: Handlers['onReturnToComparison']
	onOpenUrl: (url: string) => void
	onLogScopedEvent: OnLogEvent
}

export const TermsAndConditions = ({
	insuranceComparison,
	selectedInsurer,
	onReturnToComparison,
	onOpenUrl,
	onLogScopedEvent,
}: Props) => {
	const { formatMessage } = useIntl()
	const { showCard } = useModal()

	return (
		<Card>
			<Header>
				<Title>
					<FormattedMessage
						id='opportunityCarInsurance.termsAndConditions.title'
						defaultMessage='Compare all conditions'
					/>
				</Title>
			</Header>
			<p role='text'>
				<FormattedMessage
					id='opportunityCarInsurance.termsAndConditions.descriptionOne'
					defaultMessage='Here you can compare full terms and conditions and coverages.'
				/>
			</p>
			<small>
				<FormattedMessage
					id='opportunityCarInsurance.termsAndConditions.descriptionTwo'
					defaultMessage='The data below is based on the current price and conditions from poliswijzer.nl and may differ from your current coverage.'
				/>
			</small>
			<Content>
				{insuranceComparison.policies.map((policy, index) => (
					<ButtonLink
						key={index}
						label={policies[policy.name] ?? ''}
						icon={<IconInfo />}
						onClick={() => {
							onLogScopedEvent('TERMS_AND_CONDITIONS_SHOW_POLICY', { policy: policy.name })
							showCard((props) => (
								<Modal
									insurances={insuranceComparison.insurances}
									title={policies[policy.name] ?? ''}
									{...policy}
									{...props}
								/>
							))
						}}
					/>
				))}
			</Content>
			<ButtonGroup spacing={8} center column>
				<ButtonSolid
					label={
						<ButtonLabel>
							<FormattedMessage
								id='opportunityCarInsurance.termsAndConditions.buttonContinue'
								defaultMessage='Continue switching'
							/>
							<IconLink />
						</ButtonLabel>
					}
					aria-label={`
							${formatMessage({ id: 'opportunityCarInsurance.termsAndConditions.buttonContinue', defaultMessage: 'Continue switching' })}.
							${formatMessage({ id: 'buttonExternal.annotation', defaultMessage: 'You are leaving the app and will be taken to the browser.' })}
						`}
					onClick={() => {
						onLogScopedEvent('CALCULATE_PREMIUM')
						onOpenUrl('https://www.abnamro.nl/mijn-abnamro/verzekeren/autoverzekering/aanvragen?pos=carefree_car')
					}}
				/>
				<ButtonText
					label={<FormattedMessage id='opportunityCarInsurance.buttonBack' defaultMessage='Back' />}
					onClick={() => onReturnToComparison(insuranceComparison, selectedInsurer)}
				/>
			</ButtonGroup>
		</Card>
	)
}
